import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import { Divider } from '@react-md/divider';
import { Typography } from '@react-md/typography';
import { Icons } from '@components/icons';
import { Label } from '@components/label';
import { FADE_STAGGER } from '@components/animations';
import { FeatureContent } from '@components/feature-content';
import { Order } from '@types';
import { InvoiceTileHead } from '@components/invoice-title-head';
import { DateTransform } from '@components/date';
import { partsId } from '../helpers';
import { InvoicePart } from './invoice-part';

import styles from './invoice-parts.module.scss';

interface InvoicePartsProps {
    order: Order;
    className?: string;
}

export const InvoiceParts: React.FC<InvoicePartsProps> = ({ className, children, order }) => {
    const hasAtLeastOneBatchDiscount = order.line_items.some(lineItem => Number(lineItem.discount_percents) !== 0);
    const { t } = useTranslation();

    const { company_order_id: orderId, status_display: statusDisplay, date: createdAt, placed_at: placedAt } = order;

    return (
        <div className={cn('rounded-box', className)}>
            {orderId && (
                <>
                    <div className={cn('base-paddings', styles.header)}>
                        <div className={styles['title-box']}>
                            <Typography className={cn('invoice-no-icon-gap', styles.title)} type="headline-3">
                                <span>{t('titles.order', 'Order #{{orderId}}', { orderId })}</span>
                            </Typography>
                            <div>
                                {createdAt && (
                                    <Typography className="invoice-no-icon-gap" type="body-2">
                                        <span>
                                            <FeatureContent
                                                contentKey={partsId('created_date_label')}
                                                fallback={t('labels.created', 'Created')}
                                            />
                                            :{' '}
                                        </span>
                                        <DateTransform dateString={createdAt} />
                                    </Typography>
                                )}
                                {placedAt && (
                                    <Typography className="invoice-no-icon-gap" type="body-2">
                                        <span>
                                            <FeatureContent
                                                contentKey={partsId('placed_date_label')}
                                                fallback={t('labels.placed', 'Placed')}
                                            />
                                            :{' '}
                                        </span>
                                        <DateTransform dateString={placedAt} />
                                    </Typography>
                                )}
                            </div>
                        </div>
                        <Typography className={styles.status} type="body-2">
                            <span>Status:</span> <b>{statusDisplay}</b>
                        </Typography>
                    </div>
                    <Divider />
                </>
            )}

            <InvoiceTileHead
                id={partsId('title')}
                className={cn('base-paddings', { 'no-top': Boolean(orderId) })}
                icon={Icons.Box}
            >
                <FeatureContent contentKey={partsId('title')} fallback={t('titles.parts', 'Parts')} />
            </InvoiceTileHead>

            <div className={cn('overflow-x-auto', styles.table)}>
                <table>
                    <thead className={styles.head}>
                        <tr>
                            <td className={cn(styles.cell, styles.counter)} />

                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label)}>
                                    {t('labels.productModel', 'Model')}
                                </Label>
                            </td>
                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label)}>
                                    {t('labels.material', 'Material')}
                                </Label>
                            </td>
                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label)}>
                                    {t('labels.productSpecification', 'Specification')}
                                </Label>
                            </td>
                            {hasAtLeastOneBatchDiscount && (
                                <td className={styles.cell}>
                                    <Label className={cn('small-margin-bottom', styles.label, 'rmd-typography--right')}>
                                        {t('labels.discount', 'Discount')}
                                    </Label>
                                </td>
                            )}
                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label, 'rmd-typography--right')}>
                                    {t('labels.productPricePerPart', 'Price per part')}
                                </Label>
                            </td>
                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label, 'rmd-typography--right')}>
                                    {t('labels.quantity', 'Quantity')}
                                </Label>
                            </td>
                            <td className={styles.cell}>
                                <Label className={cn('small-margin-bottom', styles.label, 'rmd-typography--right')}>
                                    {t('labels.total', 'Total')}
                                </Label>
                            </td>
                        </tr>
                    </thead>

                    <TransitionGroup className={styles.body} component="tbody">
                        {order.line_items.map((purchase, i) => (
                            <InvoicePart
                                key={purchase.id}
                                purchase={purchase}
                                hasAtLeastOneBatchDiscount={hasAtLeastOneBatchDiscount}
                                delay={FADE_STAGGER * i}
                            />
                        ))}
                    </TransitionGroup>
                </table>
            </div>

            {children}
        </div>
    );
};
